import React, { useState, useEffect } from 'react';
import { 
  Layers, 
  Plus, 
  Edit3,
  Trash2,
  Save,
  X,
  Palette
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { sectorsApi } from '../lib/api';

const SectorCard = ({ sector, onEdit, onDelete }) => (
  <Card className="card-hover" data-testid={`sector-card-${sector.id}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${sector.colors?.primary}20` }}
          >
            <div 
              className="w-6 h-6 rounded-full"
              style={{ backgroundColor: sector.colors?.primary }}
            />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-slate-900">{sector.name}</h3>
            <p className="text-sm text-slate-500 mt-1">{sector.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onEdit(sector)}
            data-testid={`edit-sector-${sector.id}`}
          >
            <Edit3 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onDelete(sector)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid={`delete-sector-${sector.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {sector.keywords?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {sector.keywords.map((keyword, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {keyword}
            </Badge>
          ))}
        </div>
      )}
      
      <div className="mt-4 flex items-center gap-2">
        <Palette className="w-4 h-4 text-slate-400" />
        <div 
          className="w-6 h-6 rounded border border-slate-200"
          style={{ backgroundColor: sector.colors?.primary }}
          title="Color primario"
        />
        <div 
          className="w-6 h-6 rounded border border-slate-200"
          style={{ backgroundColor: sector.colors?.secondary }}
          title="Color secundario"
        />
      </div>
    </CardContent>
  </Card>
);

const SectorForm = ({ sector, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: sector?.id || '',
    name: sector?.name || '',
    description: sector?.description || '',
    keywords: sector?.keywords?.join(', ') || '',
    primaryColor: sector?.colors?.primary || '#4F46E5',
    secondaryColor: sector?.colors?.secondary || '#10B981',
  });
  const [loading, setLoading] = useState(false);
  const isEdit = !!sector?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setLoading(true);
    try {
      const data = {
        id: formData.id || formData.name.toLowerCase().replace(/\s+/g, '-'),
        name: formData.name,
        description: formData.description,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
        colors: {
          primary: formData.primaryColor,
          secondary: formData.secondaryColor,
        },
      };

      if (isEdit) {
        await sectorsApi.update(sector.id, data);
        toast.success('Sector actualizado');
      } else {
        await sectorsApi.create(data);
        toast.success('Sector creado');
      }
      
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Nombre del sector</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: E-commerce"
          className="mt-2"
          data-testid="sector-name-input"
        />
      </div>

      <div>
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe este sector..."
          className="mt-2"
          data-testid="sector-description-input"
        />
      </div>

      <div>
        <Label htmlFor="keywords">Palabras clave (separadas por coma)</Label>
        <Input
          id="keywords"
          value={formData.keywords}
          onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
          placeholder="productos, ofertas, envío..."
          className="mt-2"
          data-testid="sector-keywords-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="primaryColor">Color primario</Label>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="color"
              id="primaryColor"
              value={formData.primaryColor}
              onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
              className="w-10 h-10 rounded cursor-pointer"
              data-testid="sector-primary-color"
            />
            <Input
              value={formData.primaryColor}
              onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="secondaryColor">Color secundario</Label>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="color"
              id="secondaryColor"
              value={formData.secondaryColor}
              onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
              className="w-10 h-10 rounded cursor-pointer"
              data-testid="sector-secondary-color"
            />
            <Input
              value={formData.secondaryColor}
              onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <DialogFooter className="mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading} data-testid="save-sector-btn">
          {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const Sectors = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [deletingSector, setDeletingSector] = useState(null);

  const fetchSectors = async () => {
    try {
      const response = await sectorsApi.list();
      setSectors(response.data.sectors);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  const handleEdit = (sector) => {
    setEditingSector(sector);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingSector) return;
    
    try {
      await sectorsApi.delete(deletingSector.id);
      toast.success('Sector eliminado');
      fetchSectors();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeletingSector(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSector(null);
  };

  const handleFormSave = () => {
    handleFormClose();
    fetchSectors();
  };

  return (
    <div className="p-8 animate-fade-in" data-testid="sectors-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Sectores</h1>
          <p className="text-slate-500 mt-1">Gestiona los sectores e industrias para tus campañas</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-sector-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Sector
        </Button>
      </div>

      {/* Sectors Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-6 bg-slate-100 rounded animate-pulse w-1/3 mb-2" />
                <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sectors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sectors.map((sector) => (
            <SectorCard
              key={sector.id}
              sector={sector}
              onEdit={handleEdit}
              onDelete={setDeletingSector}
            />
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay sectores</h3>
          <p className="text-slate-500 mb-4">Crea tu primer sector para categorizar tus campañas</p>
          <Button onClick={() => setShowForm(true)} data-testid="create-first-sector-btn">
            <Plus className="w-4 h-4 mr-2" />
            Crear sector
          </Button>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSector ? 'Editar Sector' : 'Nuevo Sector'}</DialogTitle>
            <DialogDescription>
              {editingSector ? 'Modifica los datos del sector' : 'Crea un nuevo sector para tus campañas'}
            </DialogDescription>
          </DialogHeader>
          <SectorForm
            sector={editingSector}
            onSave={handleFormSave}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingSector} onOpenChange={() => setDeletingSector(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sector?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El sector "{deletingSector?.name}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sectors;
